import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, ConfigProvider, Modal, message, theme as antdTheme } from 'antd';
import { collectFromDataTransfer, collectFromFileList } from '../../utils/treeUpload/collectEntries';
import { ARCHIVE_ACCEPT } from '../../utils/treeUpload/readArchive';
import { formatBytes } from '../../utils/treeUpload/uploadPlan';
import { collectSource, prepareImport, runImport, SKIP_LABELS } from '../../utils/projectImport';
import { MAX_DESCRIPTION_LENGTH } from '../../utils/detectServices';
import { openProject } from '../../utils/openProject';
import { resolveTenantId, validateProjectName, MAX_NAME_LENGTH } from './NewProjectDialog';
import './sandbox.css';
import './NewProjectDialog.css';
import './ImportProjectDialog.css';

const SENT = new Set(['created', 'overwritten', 'renamed', 'skipped', 'failed', 'cancelled', 'packed', 'sending']);

/** "web: Node.js · 42 files" */
const describeService = (service) => [service.kinds.join(', ') || 'Files', `${service.fileCount} file${service.fileCount === 1 ? '' : 's'}`].join(' · ');

/**
 * New project › "Import a folder or zip": pick a folder or a .zip (or drop one), see the services Flow found and where
 * they go (`services/<name>`), the name and description read from the README or package.json (editable), then the
 * project is created, its files sent (progress, Cancel), and it opens on its Dashboard, ready to Start.
 *
 * `initialCollected` (a drop on the Home prompt): what was dropped, collectEntries' shape; the dialog starts from it.
 */
export default function ImportProjectDialog({ open, onClose, selectedTenant = null, initialCollected = null }) {
  const navigate = useNavigate();
  const folderInputRef = useRef(null);
  const archiveInputRef = useRef(null);
  const abortRef = useRef(null);
  const [step, setStep] = useState('pick'); // pick | reading | review | importing | stopped
  const [dragOver, setDragOver] = useState(false);
  const [reading, setReading] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const reset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStep('pick');
    setReading(null);
    setPrepared(null);
    setName('');
    setDescription('');
    setTouched(false);
    setError(null);
    setProgress(null);
    setOutcome(null);
  };

  const load = async (collected) => {
    if (!collected?.files?.length) {
      setError('Nothing to import: the folder is empty or could not be read.');
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setStep('reading');
    setReading({ phase: 'reading', done: 0, total: 0 });
    try {
      const source = await collectSource(collected, { signal: controller.signal, onProgress: setReading });
      const next = await prepareImport(source);
      if (controller.signal.aborted) { return; }
      setPrepared(next);
      setName(next.info.name);
      setDescription(next.info.description);
      setStep('review');
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) { setStep('pick'); return; }
      setError(err?.message || 'This could not be read.');
      setStep('pick');
    } finally {
      if (abortRef.current === controller) { abortRef.current = null; }
    }
  };

  useEffect(() => {
    if (!open) { reset(); return; }
    if (initialCollected) { load(initialCollected); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCollected]);

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    collectFromDataTransfer(event.dataTransfer).then(load).catch((err) => setError(err?.message || 'The drop could not be read.'));
  };

  const onPicked = (event) => {
    const collected = collectFromFileList(event.target.files);
    event.target.value = '';
    load(collected);
  };

  const validation = validateProjectName(name);
  const plan = prepared?.plan;
  const canImport = step === 'review' && plan && plan.files.length > 0 && plan.problems.length === 0 && !validation;

  const start = async (event) => {
    event?.preventDefault();
    setTouched(true);
    if (!canImport) { return; }
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setStep('importing');
    setProgress({ phase: 'creating', results: {} });
    try {
      const tenantId = await resolveTenantId(selectedTenant);
      if (!tenantId) { throw new Error('Your account has no workspace to create projects in yet.'); }
      const result = await runImport({ tenantId, name: name.trim(), description: description.trim(), prepared, signal: controller.signal, onProgress: setProgress });
      abortRef.current = null;
      if (!result.projectId) { setStep('review'); return; }
      if (result.cancelled || result.failed) {
        setOutcome(result);
        setStep('stopped');
        return;
      }
      onClose();
      openProject({ project: { _id: result.projectId, name: result.name, tenantId: result.tenantId }, navigate });
      const count = result.services.length;
      message.success(`Imported ${count} service${count === 1 ? '' : 's'}. Start ${count === 1 ? 'it' : 'them'} from the Dashboard.`, 5);
    } catch (err) {
      abortRef.current = null;
      setError(err?.message || 'The project could not be created. Try again.');
      setStep('review');
    }
  };

  const openPartial = () => {
    const { projectId, name: createdName, tenantId } = outcome;
    onClose();
    openProject({ project: { _id: projectId, name: createdName, tenantId }, navigate });
  };

  const busy = step === 'reading' || step === 'importing';
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

  const results = progress?.results || {};
  const sent = Object.values(results).filter((line) => SENT.has(line.status)).length;
  const importPercent = plan?.files.length ? Math.round((sent / plan.files.length) * 95) : 0;
  const readPercent = reading?.total ? Math.round((reading.done / reading.total) * 100) : 0;
  const excludedCounts = (prepared?.excluded || []).reduce((counts, line) => ({ ...counts, [line.reason]: (counts[line.reason] || 0) + 1 }), {});
  const shownNameError = touched ? validation : null;

  return (
    <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Modal
        className="sbx-new-project sbx-import"
        destroyOnHidden
        footer={null}
        mask={{ closable: !busy }}
        onCancel={busy ? undefined : onClose}
        open={open}
        title="Import a folder or zip"
        width={520}
      >
        {step === 'pick' && (
          <div>
            <p className="sbx-new-project-hint">A new project from code you have: Flow finds its services and puts each under <code>services/</code>. node_modules, .git and build output are left out.</p>
            <div
              className={`sbx-import-drop${dragOver ? ' is-over' : ''}`}
              data-testid="import-drop"
              onDragLeave={() => setDragOver(false)}
              onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
              onDrop={onDrop}
            >
              <p className="sbx-import-drop-title">Drop a folder or a .zip here</p>
              <div className="sbx-import-drop-actions">
                <Button onClick={() => folderInputRef.current?.click()}>Choose a folder…</Button>
                <Button onClick={() => archiveInputRef.current?.click()}>Choose a .zip…</Button>
              </div>
              <p className="sbx-import-drop-note">.zip or .tar.gz, up to 200 MB</p>
            </div>
            <input data-testid="import-folder-input" hidden multiple onChange={onPicked} ref={folderInputRef} type="file" webkitdirectory="" />
            <input accept={ARCHIVE_ACCEPT} data-testid="import-archive-input" hidden onChange={onPicked} ref={archiveInputRef} type="file" />
            {error && <p className="sbx-new-project-error" role="alert">{error}</p>}
            <div className="sbx-new-project-actions">
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </div>
        )}

        {step === 'reading' && (
          <div>
            <p className="sbx-new-project-hint">{reading?.phase === 'unpacking' ? 'Unpacking…' : 'Reading…'}</p>
            <div aria-label="Reading" aria-valuemax={100} aria-valuemin={0} aria-valuenow={readPercent} className="sbx-import-bar" role="progressbar">
              <div className="sbx-import-bar-fill" style={{ width: `${readPercent}%` }} />
            </div>
            <div className="sbx-new-project-actions">
              <Button onClick={() => abortRef.current?.abort()}>Cancel</Button>
            </div>
          </div>
        )}

        {(step === 'review' || step === 'importing') && prepared && (
          <form noValidate onSubmit={start}>
            <label className="sbx-new-project-label" htmlFor="sbx-import-name">Project name</label>
            <input
              aria-invalid={shownNameError ? 'true' : 'false'}
              autoComplete="off"
              className={`sbx-new-project-input${shownNameError ? ' invalid' : ''}`}
              disabled={step !== 'review'}
              id="sbx-import-name"
              maxLength={MAX_NAME_LENGTH + 20}
              onChange={(event) => setName(event.target.value)}
              type="text"
              value={name}
            />
            {shownNameError && <p className="sbx-new-project-field-error">{shownNameError}</p>}
            <label className="sbx-new-project-label sbx-import-gap" htmlFor="sbx-import-description">Description</label>
            <textarea
              className="sbx-new-project-input sbx-import-description"
              disabled={step !== 'review'}
              id="sbx-import-description"
              maxLength={MAX_DESCRIPTION_LENGTH}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What it does (optional)"
              rows={2}
              value={description}
            />

            <p className="sbx-new-project-label sbx-import-gap">
              {prepared.layout.services.length === 1 ? 'One service' : `${prepared.layout.services.length} services`}
              <span className="sbx-import-muted"> · {plan.files.length} file{plan.files.length === 1 ? '' : 's'}, {formatBytes(plan.totalBytes)}</span>
            </p>
            <ul className="sbx-import-services" data-testid="import-services">
              {prepared.layout.services.map((service) => (
                <li key={service.name}>
                  <code>services/{service.name}</code>
                  <span className="sbx-import-muted">{service.source && service.source !== `services/${service.name}` ? `from ${service.source}/ · ` : ''}{describeService(service)}</span>
                </li>
              ))}
            </ul>
            {prepared.layout.mode === 'multi' && plan.files.some((file) => !file.relPath.startsWith('services/')) && (
              <p className="sbx-import-muted">Files outside the services (README, docs) go to the project’s root.</p>
            )}
            {Object.keys(excludedCounts).length > 0 && (
              <ul className="sbx-import-notes">
                {Object.entries(excludedCounts).map(([reason, count]) => <li key={reason}>{count} left out: {SKIP_LABELS[reason] || reason}</li>)}
              </ul>
            )}
            {prepared.unreadable?.length > 0 && (
              <ul className="sbx-import-notes">
                {prepared.unreadable.slice(0, 3).map((line) => <li key={line.relPath}>{line.relPath}: {line.error}</li>)}
              </ul>
            )}
            {plan.problems.map((problem) => <p className="sbx-new-project-error" key={problem} role="alert">{problem}</p>)}
            {plan.files.length === 0 && <p className="sbx-new-project-error" role="alert">Nothing to import: every file was left out.</p>}
            {plan.warnings.map((warning) => <p className="sbx-import-muted" key={warning}>{warning}</p>)}
            {error && <p className="sbx-new-project-error" role="alert">{error}</p>}

            {step === 'importing' && (
              <>
                <p className="sbx-import-muted">{progress?.phase === 'creating' ? 'Creating the project…' : progress?.phase === 'sending' ? 'Sending to your sandbox…' : 'Packing files…'}</p>
                <div aria-label="Importing" aria-valuemax={100} aria-valuemin={0} aria-valuenow={importPercent} className="sbx-import-bar" role="progressbar">
                  <div className={`sbx-import-bar-fill${progress?.phase === 'sending' ? ' is-busy' : ''}`} style={{ width: `${importPercent}%` }} />
                </div>
              </>
            )}
            <div className="sbx-new-project-actions">
              {step === 'review' && <Button onClick={reset}>Back</Button>}
              {step === 'review' && <Button disabled={!canImport} htmlType="submit" type="primary">Import</Button>}
              {step === 'importing' && <Button onClick={() => abortRef.current?.abort()}>Cancel import</Button>}
            </div>
          </form>
        )}

        {step === 'stopped' && outcome && (
          <div>
            <p className="sbx-new-project-error" role="alert">
              {outcome.cancelled ? 'Import cancelled.' : `${outcome.failed} file${outcome.failed === 1 ? '' : 's'} could not be imported.`}
              {` The project “${outcome.name}” was created with what arrived.`}
              {outcome.serverMayHaveSaved ? ' Part of what was being sent may have arrived too.' : ''}
            </p>
            <div className="sbx-new-project-actions">
              <Button onClick={onClose}>Close</Button>
              <Button onClick={openPartial} type="primary">Open the project</Button>
            </div>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
}

ImportProjectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedTenant: PropTypes.object,
  initialCollected: PropTypes.object,
};
